import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';
import KakaoShareButton from '../../components/feature/KakaoShareButton';
import { useCart } from '../../context/CartContext';
import { bestProducts } from '../../mocks/products';

const WALLET_ADDRESS = '0x10881B8AF28627474857e8dFedBEB6194433af42';
const SMS_RECIPIENT = '01053308579';

interface ProductPackage {
  id: string;
  productId: number;
  name: string;
  weight: string;
  krwPrice: number;
  usdtPrice: number;
  image: string;
  badge?: string;
  popular?: boolean;
}

const PRODUCT_PACKAGES: ProductPackage[] = [
  {
    id: 'pkg-yakson-2',
    productId: 1,
    name: '약손콩이 200mlX2병',
    weight: '2병 세트',
    krwPrice: 50000,
    usdtPrice: 32,
    image: 'https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/e638276f-9e94-4716-bc0b-c598f1919967_YAKSONKONGE_11.jpg?v=2b745acb2616ef0d1c374f92935c8f51',
    badge: '인기',
  },
  {
    id: 'pkg-yakson-5',
    productId: 2,
    name: '약손콩이 200mlX5병',
    weight: '5병 세트',
    krwPrice: 100000,
    usdtPrice: 65,
    image: 'https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/e638276f-9e94-4716-bc0b-c598f1919967_YAKSONKONGE_11.jpg?v=2b745acb2616ef0d1c374f92935c8f51',
    badge: '베스트',
    popular: true,
  },
  {
    id: 'pkg-spirulina-box',
    productId: 3,
    name: '스피루리나 200mgX3,600정 1BOX',
    weight: '3,600정 1BOX',
    krwPrice: 250000,
    usdtPrice: 160,
    image: 'https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/39229809-5b7c-4182-a3dc-611e808ad51d___1_1.jpg?v=22f7d1d2f402b06ac30da9e380ca1fa7',
    badge: '대용량',
  },
];

type Step = 'select' | 'pay' | 'complete';

interface UserForm {
  name: string;
  phone: string;
  email: string;
}

export default function PointPurchasePage() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [step, setStep] = useState<Step>('select');
  const [selectedPkg, setSelectedPkg] = useState<ProductPackage | null>(null);
  const [copiedWallet, setCopiedWallet] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [txHashError, setTxHashError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderNumber] = useState(() => 'PT-' + Date.now().toString().slice(-8));
  const [form, setForm] = useState<UserForm>({ name: '', phone: '', email: '' });
  const [formErrors, setFormErrors] = useState<Partial<UserForm>>({});

  const formatKRW = (n: number) => n.toLocaleString('ko-KR');

  const handleCopyWallet = () => {
    navigator.clipboard.writeText(WALLET_ADDRESS).then(() => {
      setCopiedWallet(true);
      setTimeout(() => setCopiedWallet(false), 2000);
    });
  };

  const validateForm = (): boolean => {
    const errs: Partial<UserForm> = {};
    if (!form.name.trim()) errs.name = '이름을 입력해주세요.';
    if (!form.phone.trim()) errs.phone = '연락처를 입력해주세요.';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleProceedToPay = () => {
    if (!selectedPkg) return;
    if (!validateForm()) return;
    setStep('pay');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!txHash.trim()) {
      setTxHashError('트랜잭션 해시(TX Hash)를 입력해주세요.');
      return;
    }
    if (txHash.trim().length < 60) {
      setTxHashError('유효하지 않은 트랜잭션 해시입니다. 올바른 TX Hash를 입력해주세요.');
      return;
    }
    setTxHashError(null);
    setIsSubmitting(true);

    const formData = new URLSearchParams();
    formData.append('orderNumber', orderNumber);
    formData.append('name', form.name);
    formData.append('phone', form.phone);
    formData.append('email', form.email);
    formData.append('paymentType', 'USDT 결제');
    formData.append('product', selectedPkg?.name ?? '');
    formData.append('krwPrice', `${formatKRW(selectedPkg?.krwPrice ?? 0)}원`);
    formData.append('usdtAmount', `${selectedPkg?.usdtPrice} USDT`);
    formData.append('walletAddress', WALLET_ADDRESS);
    formData.append('txHash', txHash.trim());

    try {
      await fetch('https://readdy.ai/api/form/d6ks1ho5dsf7sr1n5lag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch (err) {
      void err;
    }

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
      await fetch(`${supabaseUrl}/functions/v1/sms-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          to: SMS_RECIPIENT,
          orderNumber,
          name: form.name,
          phone: form.phone,
          totalPrice: `${selectedPkg?.usdtPrice} USDT (${formatKRW(selectedPkg?.krwPrice ?? 0)}원)`,
          txHash: txHash.trim(),
          items: `${selectedPkg?.name} (${selectedPkg?.weight})`,
        }),
      });
    } catch (err) {
      void err;
    }

    // 장바구니에도 담기
    const matchedProduct = bestProducts.find((p) => p.id === selectedPkg?.productId);
    if (matchedProduct) {
      addToCart({
        id: String(matchedProduct.id),
        name: matchedProduct.name,
        price: matchedProduct.price,
        originalPrice: matchedProduct.originalPrice,
        image: matchedProduct.image,
        category: matchedProduct.category,
      }, 1);
    }

    setIsSubmitting(false);
    setStep('complete');
  };

  if (step === 'complete') {
    const shareTitle = `✅ USDT 결제 신청 완료 — ${orderNumber}`;
    const shareDesc = [
      txHash ? `TX Hash: ${txHash}` : '',
      `USDT 지갑: ${WALLET_ADDRESS}`,
    ].filter(Boolean).join('\n');

    const shareItems = selectedPkg
      ? [{ name: selectedPkg.name, quantity: 1, price: selectedPkg.krwPrice ?? 0 }]
      : [];

    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 pt-20 pb-16 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-8 md:p-12 max-w-md w-full text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-teal-50 rounded-full">
              <i className="ri-checkbox-circle-fill text-4xl text-teal-500 w-10 h-10 flex items-center justify-center"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              USDT 결제 신청 완료!
            </h2>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">
              TX Hash가 확인되면 주문이 처리됩니다.<br />
              관리자 확인 후 빠르게 배송해드립니다.
            </p>
            <div className="bg-gray-50 rounded-xl p-5 mb-5 text-left space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">주문번호</span>
                <span className="font-bold text-gray-900">{orderNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">주문자</span>
                <span className="font-semibold text-gray-900">{form.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">상품</span>
                <span className="font-semibold text-gray-900">{selectedPkg?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">결제 금액</span>
                <div className="text-right">
                  <p className="font-bold text-teal-600">{selectedPkg?.usdtPrice} USDT</p>
                  <p className="text-xs text-gray-400">{formatKRW(selectedPkg?.krwPrice ?? 0)}원</p>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200 space-y-1.5">
                <p className="text-xs text-gray-500">제출된 TX Hash</p>
                <p className="text-xs font-mono text-gray-700 break-all">{txHash}</p>
                <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                  <i className="ri-message-2-line w-3 h-3 flex items-center justify-center"></i>
                  관리자(010-5330-8579)에게 문자로 전송되었습니다.
                </p>
              </div>
            </div>

            {/* 지갑 주소 재확인 */}
            <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4 mb-5 text-left">
              <p className="text-xs font-bold text-teal-700 mb-3 flex items-center gap-1.5">
                <i className="ri-wallet-3-line w-3.5 h-3.5 flex items-center justify-center"></i>
                USDT 전송 지갑 주소 재확인
              </p>
              <div className="flex items-center gap-4">
                {/* QR코드 */}
                <div className="flex-shrink-0 flex flex-col items-center gap-1.5">
                  <div className="w-20 h-20 bg-white rounded-xl border border-teal-200 flex items-center justify-center overflow-hidden">
                    <img
                      src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=0x10881B8AF28627474857e8dFedBEB6194433af42&format=png&margin=1"
                      alt="USDT 지갑 QR코드"
                      className="w-full h-full object-contain"
                      width={80}
                      height={80}
                    />
                  </div>
                  <p className="text-[9px] text-teal-500 font-semibold text-center leading-tight">
                    QR 스캔
                  </p>
                </div>
                {/* 주소 정보 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-black bg-teal-500 text-white">USDT</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-teal-100 text-teal-700">ERC-20</span>
                  </div>
                  <p className="text-[11px] font-mono font-semibold text-teal-800 break-all leading-relaxed select-all">
                    {WALLET_ADDRESS}
                  </p>
                  <p className="text-[10px] text-teal-500 mt-1.5 flex items-center gap-1">
                    <i className="ri-information-line w-3 h-3 flex items-center justify-center"></i>
                    전송 미완료 시 위 주소로 전송해주세요
                  </p>
                </div>
              </div>
            </div>

            {/* 카카오톡 공유 */}
            <div className="mb-3">
              <KakaoShareButton
                title={shareTitle}
                description={shareDesc}
                orderNumber={orderNumber}
                items={shareItems}
                totalAmount={`${selectedPkg?.usdtPrice} USDT (${formatKRW(selectedPkg?.krwPrice ?? 0)}원)`}
                paymentMethod="USDT 포인트결제"
              />
            </div>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/order')}
                className="block w-full py-3 bg-teal-500 text-white rounded-full font-semibold hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                주문 페이지로 이동
              </button>
              <button
                onClick={() => { setStep('select'); setTxHash(''); setSelectedPkg(null); }}
                className="block w-full py-3 border-2 border-teal-200 text-teal-600 rounded-full font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                다른 제품 추가 구매
              </button>
              <Link
                to="/"
                className="block w-full py-3 border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:border-teal-400 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                홈으로 돌아가기
              </Link>
            </div>
          </div>
        </div>
        <PageFooter />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 pt-20 pb-16">
        {/* 헤더 배너 */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/7a0630877d2345c0516a9fa5c66d6882.jpg"
              alt="USDT 결제 배너"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 via-teal-900/75 to-teal-800/60"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              {/* 왼쪽: 텍스트 */}
              <div className="flex-1 text-white">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-teal-400/30 text-teal-100 border border-teal-400/40">
                    USDT · ERC-20
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-white border border-white/20">
                    Global AI Trade
                  </span>
                </div>
                <h1 className="text-2xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                  USDT로 제품 구매하기
                </h1>
                <p className="text-teal-100 text-sm md:text-base leading-relaxed mb-5">
                  원하는 제품을 선택하고 USDT를 지갑 주소로 전송하시면 됩니다.<br />
                  ERC-20 네트워크 전용입니다.
                </p>
                {/* 지갑 주소 */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3">
                  <p className="text-[10px] text-teal-300 font-semibold mb-1 uppercase tracking-wider">USDT 지갑 주소 (ERC-20)</p>
                  <div className="flex items-center gap-2">
                    <i className="ri-wallet-3-line text-teal-300 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                    <span className="text-xs font-mono text-white break-all leading-relaxed">{WALLET_ADDRESS}</span>
                  </div>
                </div>
              </div>

              {/* 오른쪽: QR코드 카드 */}
              <div className="flex-shrink-0">
                <div className="bg-white rounded-2xl p-4 text-center w-52">
                  <p className="text-xs font-bold text-gray-600 mb-3 flex items-center justify-center gap-1.5">
                    <i className="ri-qr-code-line text-teal-500 w-4 h-4 flex items-center justify-center"></i>
                    QR 스캔으로 전송
                  </p>
                  <div className="w-36 h-36 mx-auto rounded-xl overflow-hidden bg-white flex items-center justify-center border border-gray-100">
                    <img
                      src="https://api.qrserver.com/v1/create-qr-code/?size=144x144&data=0x10881B8AF28627474857e8dFedBEB6194433af42&format=png&margin=2"
                      alt="USDT 지갑 주소 QR코드"
                      className="w-full h-full object-contain"
                      width={144}
                      height={144}
                    />
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-bold">
                      <i className="ri-bit-coin-line w-3 h-3 flex items-center justify-center"></i>
                      USDT Only
                    </span>
                    <p className="text-[10px] text-gray-400 mt-1.5 leading-relaxed">
                      ERC-20 네트워크로만<br />전송하세요
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
          {/* 단계 표시 */}
          <div className="flex items-center justify-center gap-3 mb-10">
            {(['select', 'pay'] as Step[]).map((s, idx) => (
              <div key={s} className="flex items-center gap-3">
                <div className={`flex items-center gap-2 ${step === s ? 'text-teal-600' : s === 'select' && step === 'pay' ? 'text-teal-400' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step === s ? 'bg-teal-500 text-white' : s === 'select' && step === 'pay' ? 'bg-teal-100 text-teal-600' : 'bg-gray-200 text-gray-500'}`}>
                    {s === 'select' && step === 'pay'
                      ? <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                      : idx + 1}
                  </div>
                  <span className="text-sm font-semibold hidden sm:block">
                    {s === 'select' ? '제품 선택' : 'USDT 전송'}
                  </span>
                </div>
                {idx < 1 && <div className="w-10 h-px bg-gray-300"></div>}
              </div>
            ))}
          </div>

          {/* STEP 1: 제품 선택 + 사용자 정보 */}
          {step === 'select' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                  구매할 제품 선택
                </h2>
                <p className="text-sm text-gray-500 mb-6">아래 제품 중 구매하실 제품을 선택해주세요.</p>

                <div className="grid sm:grid-cols-3 gap-5">
                  {PRODUCT_PACKAGES.map((pkg) => (
                    <button
                      key={pkg.id}
                      type="button"
                      onClick={() => setSelectedPkg(pkg)}
                      className={`relative text-left rounded-2xl border-2 overflow-hidden transition-all cursor-pointer group ${
                        selectedPkg?.id === pkg.id
                          ? 'border-teal-400 bg-white'
                          : 'border-gray-200 bg-white hover:border-teal-200'
                      }`}
                    >
                      {pkg.popular && (
                        <div className="absolute top-3 left-3 z-10">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-500 text-white whitespace-nowrap">
                            베스트
                          </span>
                        </div>
                      )}
                      {selectedPkg?.id === pkg.id && (
                        <div className="absolute top-3 right-3 z-10 w-6 h-6 flex items-center justify-center bg-teal-500 rounded-full">
                          <i className="ri-check-line text-white text-xs w-3 h-3 flex items-center justify-center"></i>
                        </div>
                      )}

                      {/* 제품 이미지 */}
                      <div className="w-full h-44 overflow-hidden bg-stone-50">
                        <img
                          src={pkg.image}
                          alt={pkg.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>

                      {/* 제품 정보 */}
                      <div className="p-4">
                        {pkg.badge && (
                          <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold mb-2">
                            {pkg.badge}
                          </span>
                        )}
                        <h3 className="text-sm font-bold text-gray-900 mb-1">{pkg.name}</h3>
                        <p className="text-xs text-gray-400 mb-3">{pkg.weight}</p>

                        {/* 가격 */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-base font-black text-gray-900">
                              {formatKRW(pkg.krwPrice)}원
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-500 text-white text-xs font-black">
                              <i className="ri-bit-coin-line w-3 h-3 flex items-center justify-center"></i>
                              USDT ${pkg.usdtPrice}
                            </span>
                            <span className="text-[10px] text-gray-400 font-medium">ERC-20</span>
                          </div>
                        </div>
                      </div>

                      {/* 선택 표시 바 */}
                      <div className={`h-1 w-full transition-all ${selectedPkg?.id === pkg.id ? 'bg-teal-400' : 'bg-transparent'}`}></div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 사용자 정보 입력 */}
              <div className="bg-white rounded-2xl p-6 md:p-8">
                <h3 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-user-3-line w-5 h-5 flex items-center justify-center text-teal-500"></i>
                  주문자 정보
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      이름 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, name: e.target.value }));
                        setFormErrors((p) => ({ ...p, name: undefined }));
                      }}
                      placeholder="홍길동"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 ${formErrors.name ? 'border-red-400' : 'border-gray-200'}`}
                    />
                    {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      연락처 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, phone: e.target.value }));
                        setFormErrors((p) => ({ ...p, phone: undefined }));
                      }}
                      placeholder="010-0000-0000"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 ${formErrors.phone ? 'border-red-400' : 'border-gray-200'}`}
                    />
                    {formErrors.phone && <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">이메일 (선택)</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                      placeholder="example@email.com"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                    />
                  </div>
                </div>
              </div>

              {/* 선택 요약 + 다음 버튼 */}
              {selectedPkg ? (
                <div className="bg-teal-600 rounded-2xl p-5 md:p-6 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-white/20">
                      <img src={selectedPkg.image} alt={selectedPkg.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-xs text-teal-200 mb-0.5">선택된 제품</p>
                      <p className="font-bold text-base">{selectedPkg.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-teal-100">{formatKRW(selectedPkg.krwPrice)}원</span>
                        <span className="text-teal-300">·</span>
                        <span className="text-sm font-black text-white">USDT ${selectedPkg.usdtPrice}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleProceedToPay}
                    className="flex-shrink-0 px-8 py-3 bg-white text-teal-600 rounded-full font-bold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    USDT 결제하기
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-gray-400">구매할 제품을 선택해주세요.</div>
              )}
            </div>
          )}

          {/* STEP 2: USDT 전송 + TX Hash 입력 */}
          {step === 'pay' && (
            <form data-readdy-form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
              {/* 선택 제품 요약 */}
              <div className="bg-white rounded-2xl overflow-hidden border-2 border-teal-200">
                <div className="flex items-center gap-4 p-5">
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-stone-50">
                    <img src={selectedPkg?.image} alt={selectedPkg?.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-400 mb-0.5">주문 상품</p>
                    <h3 className="font-bold text-gray-900">{selectedPkg?.name}</h3>
                    <p className="text-xs text-gray-500">{selectedPkg?.weight}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400 mb-0.5">결제 금액</p>
                    <p className="text-2xl font-black text-teal-600">${selectedPkg?.usdtPrice}</p>
                    <p className="text-xs text-gray-400 font-semibold">USDT</p>
                    <p className="text-xs text-gray-400 mt-0.5">{formatKRW(selectedPkg?.krwPrice ?? 0)}원</p>
                  </div>
                </div>
              </div>

              {/* 지갑 주소 */}
              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-wallet-3-line w-5 h-5 flex items-center justify-center text-teal-500"></i>
                  USDT 전송 지갑 주소
                </h3>

                {/* QR + 주소 나란히 */}
                <div className="bg-teal-50 border-2 border-teal-300 rounded-2xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-black bg-teal-500 text-white">USDT</span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-700">ERC-20 이더리움 네트워크</span>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-stretch">
                    {/* QR코드 */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-2">
                      <div className="w-36 h-36 bg-white rounded-xl border-2 border-teal-200 flex items-center justify-center overflow-hidden">
                        <img
                          src="https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=0x10881B8AF28627474857e8dFedBEB6194433af42&format=png&margin=2"
                          alt="USDT 지갑 주소 QR코드"
                          className="w-full h-full object-contain"
                          width={140}
                          height={140}
                        />
                      </div>
                      <p className="text-[10px] text-teal-600 font-semibold text-center flex items-center gap-1">
                        <i className="ri-qr-code-line w-3 h-3 flex items-center justify-center"></i>
                        카메라로 스캔
                      </p>
                    </div>

                    {/* 구분선 */}
                    <div className="hidden sm:flex flex-col items-center gap-2">
                      <div className="flex-1 w-px bg-teal-200"></div>
                      <span className="text-[10px] text-teal-400 font-semibold px-1">OR</span>
                      <div className="flex-1 w-px bg-teal-200"></div>
                    </div>
                    <div className="flex sm:hidden items-center gap-3 w-full">
                      <div className="flex-1 h-px bg-teal-200"></div>
                      <span className="text-[10px] text-teal-400 font-semibold">OR</span>
                      <div className="flex-1 h-px bg-teal-200"></div>
                    </div>

                    {/* 주소 + 복사 */}
                    <div className="flex-1 flex flex-col justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold text-teal-700 mb-2">주소 직접 복사</p>
                        <div className="bg-white border border-teal-200 rounded-xl px-3 py-3 mb-3">
                          <p className="text-xs font-mono font-semibold text-gray-800 break-all select-all leading-relaxed">
                            {WALLET_ADDRESS}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleCopyWallet}
                          className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                            copiedWallet
                              ? 'bg-teal-500 text-white'
                              : 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                          }`}
                        >
                          {copiedWallet ? (
                            <>
                              <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                              복사됨!
                            </>
                          ) : (
                            <>
                              <i className="ri-file-copy-line w-4 h-4 flex items-center justify-center"></i>
                              지갑 주소 복사
                            </>
                          )}
                        </button>
                      </div>
                      <div className="bg-white/70 rounded-lg px-3 py-2">
                        <p className="text-[10px] text-teal-600 font-semibold flex items-center gap-1">
                          <i className="ri-information-line w-3 h-3 flex items-center justify-center"></i>
                          주소를 직접 입력하지 말고 꼭 복사하여 사용하세요
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 전송 금액 안내 */}
                <div className="bg-teal-600 rounded-xl p-4 mb-4 flex items-center justify-between text-white">
                  <div>
                    <p className="text-xs text-teal-200 mb-1">정확히 이 금액을 전송하세요</p>
                    <p className="text-3xl font-black">{selectedPkg?.usdtPrice} <span className="text-lg text-teal-200 font-bold">USDT</span></p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-teal-200">원화 기준</p>
                    <p className="text-lg font-bold">{formatKRW(selectedPkg?.krwPrice ?? 0)}원</p>
                  </div>
                </div>

                {/* 주의사항 */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5">
                    <i className="ri-alert-line w-4 h-4 flex items-center justify-center"></i>
                    전송 전 반드시 확인하세요
                  </p>
                  <p className="text-xs text-amber-700 flex items-start gap-1.5">
                    <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-amber-500"></i>
                    네트워크: <strong className="ml-0.5">ERC-20 (이더리움)</strong> 네트워크로만 전송하세요.
                  </p>
                  <p className="text-xs text-amber-700 flex items-start gap-1.5">
                    <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-amber-500"></i>
                    코인: <strong className="ml-0.5">USDT (Tether)</strong> 만 지원합니다.
                  </p>
                  <p className="text-xs text-amber-700 flex items-start gap-1.5">
                    <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5 text-amber-500"></i>
                    금액: 정확히 <strong className="ml-0.5">{selectedPkg?.usdtPrice} USDT</strong>를 전송해주세요.
                  </p>
                  <p className="text-xs text-red-600 flex items-start gap-1.5 pt-1 border-t border-amber-200">
                    <i className="ri-error-warning-line w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                    다른 네트워크 또는 다른 코인 전송 시 자산이 손실될 수 있습니다.
                  </p>
                </div>
              </div>

              {/* TX Hash 입력 */}
              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <i className="ri-fingerprint-line w-5 h-5 flex items-center justify-center text-teal-500"></i>
                  트랜잭션 해시 (TX Hash) 입력
                  <span className="text-red-500 text-sm">*</span>
                </h3>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                  USDT 전송 완료 후 거래소 또는 지갑에서 확인한 TX Hash를 입력해주세요.
                  입력하신 정보는 <strong className="text-teal-600">010-5330-8579</strong>로 즉시 문자 전송됩니다.
                </p>

                <textarea
                  value={txHash}
                  onChange={(e) => { setTxHash(e.target.value); setTxHashError(null); }}
                  placeholder="0x로 시작하는 트랜잭션 해시를 입력하세요 (예: 0x3a5b7c9d...)"
                  rows={4}
                  maxLength={200}
                  className={`w-full px-4 py-3 border-2 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none transition-colors ${
                    txHashError
                      ? 'border-red-400 bg-red-50'
                      : txHash && txHash.trim().length >= 60
                      ? 'border-teal-400 bg-teal-50/30'
                      : 'border-gray-200'
                  }`}
                />

                {txHashError && (
                  <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                    <i className="ri-error-warning-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {txHashError}
                  </p>
                )}
                {txHash && !txHashError && txHash.trim().length >= 60 && (
                  <p className="text-xs text-teal-600 mt-2 flex items-center gap-1">
                    <i className="ri-checkbox-circle-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    유효한 TX Hash가 입력되었습니다.
                  </p>
                )}

                <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-start gap-2">
                  <i className="ri-message-2-line w-4 h-4 flex items-center justify-center text-gray-400 flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    주문 완료 시 주문 정보 및 TX Hash가 관리자(010-5330-8579)에게 자동으로 문자 전송됩니다. 관리자 확인 후 신속히 배송됩니다.
                  </p>
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep('select')}
                  disabled={isSubmitting}
                  className="flex-1 py-3.5 border-2 border-gray-200 text-gray-700 rounded-full font-semibold hover:border-teal-400 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  이전으로
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-[2] py-3.5 bg-teal-500 text-white rounded-full font-bold hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                      처리 중...
                    </>
                  ) : (
                    <>
                      <span className="text-xs font-black">USDT</span>
                      결제 신청 완료하기
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <PageFooter />
    </>
  );
}